/**
 * Vague 22 — Métacognition (Noam s'analyse elle-même)
 *
 * Réservé Ultra Admin. Deux onglets :
 *   1. Améliorations suggérées (Niveau 1)
 *   2. Positionnement marché (Niveau 2)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PersonaAction } from "@/lib/persona";
import {
  Brain, Sparkles, ShieldAlert, Loader2, Check, X, Clock, Trash2,
  Download, RefreshCw, ArrowUpRight, Layers, Target,
  AlertTriangle, ExternalLink, FileText, Crown, Filter,
  History, Zap, Eye, EyeOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import FreshnessBadge from "@/components/FreshnessBadge";
import { SelfStateTab, EnvironmentsTab, ReconciliationTab } from "./MetacogGovernance";

// Polling utilitaire — attend que le dernier scan async se termine
async function _pollScanComplete(maxAttempts = 40, intervalMs = 4000) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const r = await api.get("/workspace/metacognition/scan-status");
      if ((r.data?.pending_count || 0) === 0) return r.data;
    } catch { /* keep polling */ }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  return null;
}

// P0 fix V22.1 — NOAM renvoie parfois les axes en snake_case sans accents
// ("profondeur_analytique") au lieu des labels canoniques ("Profondeur analytique").
// Sans cette normalisation, le radar SVG mappe tout à 50 → polygones superposés
// (seul le dernier rendu — Hex — est visible).
function _slugifyAxis(s) {
  return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
function _scoreForAxis(compScores, axis) {
  if (!compScores || typeof compScores !== "object") return 50;
  if (compScores[axis] != null) return Number(compScores[axis]);
  // try lowercase
  if (compScores[axis.toLowerCase()] != null) return Number(compScores[axis.toLowerCase()]);
  // try slug
  const slug = _slugifyAxis(axis);
  if (compScores[slug] != null) return Number(compScores[slug]);
  // last resort : iterate keys
  for (const k of Object.keys(compScores)) {
    if (_slugifyAxis(k) === slug) return Number(compScores[k]);
  }
  return 50;
}

const PRIORITY_META = {
  P0: { color: "#E24B4A", label: "P0" },
  P1: { color: "#EF9F27", label: "P1" },
  P2: { color: "#378ADD", label: "P2" },
  backlog: { color: "#71717A", label: "Backlog" },
};
const STATUS_META = {
  new: { color: "#378ADD", label_key: "saas.metacognition.status_new", label_default: "Nouveau" },
  validated: { color: "#1D9E75", label_key: "saas.metacognition.status_validated", label_default: "Validé" },
  rejected: { color: "#E24B4A", label_key: "saas.metacognition.status_rejected", label_default: "Rejeté" },
  deferred: { color: "#A78BFA", label_key: "saas.metacognition.status_deferred", label_default: "Reporté" },
};

export default function Metacognition() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("improvements");

  // mark-seen au mount pour reset le badge sidebar
  useEffect(() => {
    api.post("/workspace/metacognition/mark-seen").catch(() => {});
    // notifie la sidebar pour refresh le badge
    window.dispatchEvent(new CustomEvent("noam:metacog-seen"));
  }, []);

  return (
    <div className="space-y-5" data-testid="metacog-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A78BFA]">
            <Crown className="w-3 h-3" /> {t("saas.metacognition.overline", { defaultValue: "Ultra Admin · Méta-Intelligence" })}
          </div>
          <h1 className="noam-display text-2xl font-bold mt-1 flex items-center gap-2">
            <Brain className="w-6 h-6 text-[#A78BFA]" />
            {t("saas.metacognition.title", { defaultValue: "Métacognition" })}
          </h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            {t(
              "saas.metacognition.subtitle",
              {
                defaultValue:
                  "Noam s'analyse elle-même. Scan hebdomadaire des évolutions mondiales (frameworks décisionnels, IA, régulations) et de son propre marché concurrentiel. Décide quelles améliorations entrent au backlog.",
              },
            )}
          </p>
        </div>
        <ModuleHelpButton moduleKey="metacognition" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10" data-testid="metacog-tabs">
        <TabBtn
          active={tab === "improvements"}
          onClick={() => setTab("improvements")}
          icon={Sparkles}
          label={t("saas.metacognition.tab_improvements", { defaultValue: "Améliorations suggérées" })}
          testid="metacog-tab-improvements"
        />
        <TabBtn
          active={tab === "market"}
          onClick={() => setTab("market")}
          icon={Target}
          label={t("saas.metacognition.tab_market", { defaultValue: "Positionnement marché" })}
          testid="metacog-tab-market"
        />
        <TabBtn
          active={tab === "self_asym"}
          onClick={() => setTab("self_asym")}
          icon={Brain}
          label={t("saas.metacognition.tab_self_asym", { defaultValue: "Asymétries de Noam" })}
          testid="metacog-tab-self-asym"
        />
        <TabBtn
          active={tab === "self_state"}
          onClick={() => setTab("self_state")}
          icon={Eye}
          label="Self-State"
          testid="metacog-tab-self-state"
        />
        <TabBtn
          active={tab === "environments"}
          onClick={() => setTab("environments")}
          icon={Layers}
          label="Environnements"
          testid="metacog-tab-environments"
        />
        <TabBtn
          active={tab === "reconciliation"}
          onClick={() => setTab("reconciliation")}
          icon={History}
          label="Réconciliation"
          testid="metacog-tab-reconciliation"
        />
      </div>

      {tab === "improvements" ? <ImprovementsTab />
        : tab === "market" ? <MarketTab />
        : tab === "self_state" ? <SelfStateTab />
        : tab === "environments" ? <EnvironmentsTab />
        : tab === "reconciliation" ? <ReconciliationTab />
        : <SelfAsymTab />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs inline-flex items-center gap-1.5 border-b-2 transition ${
        active
          ? "border-[#A78BFA] text-[#A78BFA]"
          : "border-transparent text-zinc-400 hover:text-zinc-200"
      }`}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

// ============================================================================
// TAB 1 — Améliorations suggérées
// ============================================================================
function ImprovementsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ status: "", priority: "", impact_module: "", effort: "" });
  // V22.2 — Drawer Spec IA
  const [specOpen, setSpecOpen] = useState(null); // {improvement_id, improvement_title}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/workspace/metacognition/improvements", { params });
      setItems(r.data?.items || []);
    } catch (e) {
      notify.error(fmtError(e, t("saas.metacognition.err_load", { defaultValue: "Chargement impossible" })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const scanNow = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/metacognition/scan-now", { scope: "improvements" });
      notify.success(t("saas.metacognition.toast_scan_started", { defaultValue: "Scan lancé en arrière-plan. Les nouvelles suggestions apparaîtront dans 1-2 min." }));
      // Polling court pour rafraîchir une fois terminé
      _pollScanComplete().then(() => load());
    } catch (e) { notify.error(fmtError(e, t("saas.metacognition.err_scan", { defaultValue: "Scan impossible" }))); }
    finally { setBusy(false); }
  };

  const decide = async (it, decision) => {
    try {
      await api.post(`/workspace/metacognition/improvements/${it.id}/decision`, { decision });
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(t("saas.metacognition.confirm_delete", { defaultValue: "Supprimer cette suggestion ?" }))) return;
    try {
      await api.delete(`/workspace/metacognition/improvements/${it.id}`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get("/workspace/metacognition/improvements/export-pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "noam-metacognition-backlog.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      notify.success(t("saas.metacognition.toast_export_done", { defaultValue: "PDF exporté." }));
    } catch (e) { notify.error(fmtError(e, t("saas.metacognition.err_export", { defaultValue: "Export impossible" }))); }
  };

  // V22.1 — Déduplication backfill sur les items déjà persistés
  const dedupe = async () => {
    if (!window.confirm(t("saas.metacognition.confirm_dedupe", { defaultValue: "Fusionner les doublons actifs ? Les rejetés ne sont pas touchés." }))) return;
    try {
      const r = await api.post("/workspace/metacognition/improvements/deduplicate");
      const d = r.data || {};
      notify.success(t("saas.metacognition.toast_dedupe_done", {
        defaultValue: "Dédup : {merged} doublon(s) fusionné(s), {unique} suggestions uniques.",
        merged: d.duplicates_merged || 0,
        unique: d.active_unique || 0,
      }));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  // V22.7 — Recalibration des priorités selon l'identité Noam
  const recalibrate = async () => {
    if (!window.confirm(t("saas.metacognition.confirm_recalibrate", {
      defaultValue: "Recalibrer toutes les priorités selon l'identité Noam (épistémologie/doctrine/cartographie > conformité) ? NOAM reclassifie le backlog actif en arrière-plan (~60s).",
    }))) return;
    try {
      await api.post("/workspace/metacognition/improvements/recalibrate");
      notify.success(t("saas.metacognition.toast_recalibrate_started", {
        defaultValue: "Recalibration lancée. Le backlog sera re-prioritisé dans 30-60s.",
      }));
      _pollScanComplete().then(() => load());
    } catch (e) { notify.error(fmtError(e)); }
  };

  const stats = useMemo(() => {
    const s = { new: 0, validated: 0, rejected: 0, deferred: 0 };
    items.forEach((it) => { s[it.status] = (s[it.status] || 0) + 1; });
    return s;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="metacog-improvements-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={scanNow} disabled={busy} className="btn-primary text-xs" data-testid="metacog-scan-improvements">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {t("saas.metacognition.scan_now", { defaultValue: "Scanner maintenant" })}
        </button>
        <button onClick={dedupe} className="btn-ghost text-xs" data-testid="metacog-dedupe" title={t("saas.metacognition.dedupe_title", { defaultValue: "Fusionne les suggestions répétées" })}>
          <Layers className="w-3 h-3" /> {t("saas.metacognition.dedupe", { defaultValue: "Dédupliquer" })}
        </button>
        <button
          onClick={recalibrate}
          className="btn-ghost text-xs border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10"
          data-testid="metacog-recalibrate"
          title={t("saas.metacognition.recalibrate_title", { defaultValue: "Re-prioriser selon l'identité Noam (épistémologie > conformité)" })}
        >
          <Brain className="w-3 h-3" /> {t("saas.metacognition.recalibrate", { defaultValue: "Recalibrer" })}
        </button>
        <PersonaAction action="export_pdf">
          <button onClick={exportPdf} disabled={!stats.validated} className="btn-ghost text-xs disabled:opacity-40" data-testid="metacog-export-pdf">
            <Download className="w-3 h-3" /> {t("saas.metacognition.export_pdf", { defaultValue: "Export PDF backlog" })} ({stats.validated})
          </button>
        </PersonaAction>
        <div className="flex items-center gap-1 ml-auto text-[10px] text-zinc-500" data-testid="metacog-stats">
          <span className="px-1.5 py-0.5 rounded bg-[#378ADD]/10 text-[#378ADD]">{stats.new} new</span>
          <span className="px-1.5 py-0.5 rounded bg-[#1D9E75]/10 text-[#1D9E75]">{stats.validated} val.</span>
          <span className="px-1.5 py-0.5 rounded bg-[#A78BFA]/10 text-[#A78BFA]">{stats.deferred} dif.</span>
          <span className="px-1.5 py-0.5 rounded bg-[#E24B4A]/10 text-[#E24B4A]">{stats.rejected} rej.</span>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="metacog-filters">
        <Filter className="w-3 h-3 text-zinc-500" />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="noam-input text-[11px] py-1 px-2" data-testid="metacog-filter-status">
          <option value="">{t("saas.metacognition.filter_all_status", { defaultValue: "Tous statuts" })}</option>
          <option value="new">{t("saas.metacognition.status_new", { defaultValue: "Nouveau" })}</option>
          <option value="validated">{t("saas.metacognition.status_validated", { defaultValue: "Validé" })}</option>
          <option value="rejected">{t("saas.metacognition.status_rejected", { defaultValue: "Rejeté" })}</option>
          <option value="deferred">{t("saas.metacognition.status_deferred", { defaultValue: "Reporté" })}</option>
        </select>
        <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="noam-input text-[11px] py-1 px-2" data-testid="metacog-filter-priority">
          <option value="">{t("saas.metacognition.filter_all_priority", { defaultValue: "Toutes priorités" })}</option>
          <option value="P0">P0</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="backlog">Backlog</option>
        </select>
        <select value={filters.effort} onChange={(e) => setFilters({ ...filters, effort: e.target.value })} className="noam-input text-[11px] py-1 px-2" data-testid="metacog-filter-effort">
          <option value="">{t("saas.metacognition.filter_all_effort", { defaultValue: "Tous efforts" })}</option>
          <option value="rapide">Rapide</option>
          <option value="moyen">Moyen</option>
          <option value="complexe">Complexe</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-zinc-500 text-xs py-10 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center text-zinc-500 text-sm" data-testid="metacog-improvements-empty">
          <Brain className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          {t("saas.metacognition.empty_improvements", { defaultValue: "Aucune suggestion. Lance un scan pour démarrer." })}
        </div>
      ) : (
        <div className="space-y-2" data-testid="metacog-improvements-list">
          {items.map((it) => (
            <ImprovementCard
              key={it.id}
              item={it}
              onDecide={decide}
              onDelete={remove}
              onOpenSpec={(item) => setSpecOpen({ improvement_id: item.id, improvement_title: item.title })}
            />
          ))}
        </div>
      )}

      {specOpen && (
        <SpecDrawer
          improvementId={specOpen.improvement_id}
          improvementTitle={specOpen.improvement_title}
          onClose={() => setSpecOpen(null)}
        />
      )}
    </div>
  );
}

function ImprovementCard({ item, onDecide, onDelete, onOpenSpec }) {
  const { t } = useTranslation();
  const prio = PRIORITY_META[item.priority] || PRIORITY_META.P2;
  const status = STATUS_META[item.status] || STATUS_META.new;
  return (
    <div className="noam-card p-3" data-testid={`metacog-item-${item.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider" style={{ background: `${prio.color}1A`, color: prio.color }}>
          {prio.label}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: `${status.color}1A`, color: status.color }}>
          {t(status.label_key, { defaultValue: status.label_default })}
        </span>
        {item.impact_module && (
          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-white/5 text-zinc-400">
            <Layers className="w-2.5 h-2.5 inline mr-0.5" /> {item.impact_module}
          </span>
        )}
        {item.effort && (
          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-white/5 text-zinc-400">
            <Clock className="w-2.5 h-2.5 inline mr-0.5" /> {item.effort}
          </span>
        )}
        {item.occurrences > 1 && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30"
            title={t("saas.metacognition.recurring_title", { defaultValue: "Sujet récurrent — détecté {n} fois", n: item.occurrences })}
            data-testid={`metacog-recurrence-${item.id}`}
          >
            ×{item.occurrences} {t("saas.metacognition.recurring_short", { defaultValue: "récurrent" })}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {item.status === "new" && (
            <>
              <button onClick={() => onDecide(item, "validate")} className="text-[10px] px-2 py-1 rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] hover:bg-[#1D9E75]/20 inline-flex items-center gap-1" data-testid={`metacog-validate-${item.id}`}>
                <Check className="w-3 h-3" /> {t("saas.metacognition.validate", { defaultValue: "Valider" })}
              </button>
              <button onClick={() => onDecide(item, "defer")} className="text-[10px] px-2 py-1 rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 inline-flex items-center gap-1" data-testid={`metacog-defer-${item.id}`}>
                <Clock className="w-3 h-3" /> {t("saas.metacognition.defer", { defaultValue: "Reporter" })}
              </button>
              <button onClick={() => onDecide(item, "reject")} className="text-[10px] px-2 py-1 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-[#E24B4A] hover:bg-[#E24B4A]/20 inline-flex items-center gap-1" data-testid={`metacog-reject-${item.id}`}>
                <X className="w-3 h-3" /> {t("saas.metacognition.reject", { defaultValue: "Rejeter" })}
              </button>
            </>
          )}
          {/* V22.2 — Spec IA pour validated / deferred (P0/P1 prioritaires mais dispo partout) */}
          {(item.status === "validated" || item.status === "deferred" || item.status === "new") && (
            <button
              onClick={() => onOpenSpec(item)}
              className="text-[10px] px-2 py-1 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 inline-flex items-center gap-1"
              data-testid={`metacog-spec-${item.id}`}
              title={t("saas.metacognition.spec_title", { defaultValue: "Générer / voir la spec technique IA" })}
            >
              <Sparkles className="w-3 h-3" /> {t("saas.metacognition.spec", { defaultValue: "Spec IA" })}
            </button>
          )}
          <button onClick={() => onDelete(item)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`metacog-delete-${item.id}`} title={t("common.delete", { defaultValue: "Supprimer" })}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-zinc-100 mt-2">{item.title}</h3>
      {/* V23.15.32 — Doctrine 3Q · "Et alors ?" — Impact estimé mis en évidence */}
      {item.impact_estime && (
        <div
          className="mt-2 rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/[0.08] px-2 py-1.5 flex items-start gap-1.5"
          data-testid={`metacog-impact-${item.id}`}
        >
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#1D9E75] shrink-0 mt-0.5">
            Impact
          </span>
          <span className="text-[11px] text-zinc-100 leading-snug">{item.impact_estime}</span>
        </div>
      )}
      <p className="text-xs text-zinc-300 mt-2">{item.description}</p>
      {item.feature_to_create_or_improve && (
        <div className="mt-2 text-[11px] text-zinc-400">
          <span className="text-[#EF9F27] font-medium">{t("saas.metacognition.to_do", { defaultValue: "À faire" })} :</span> {item.feature_to_create_or_improve}
        </div>
      )}
      {item.source_evolution && (
        <div className="mt-1 text-[10px] text-zinc-500 italic">
          <span className="text-zinc-400">{t("saas.metacognition.source_evolution", { defaultValue: "Évolution mondiale" })} :</span> {item.source_evolution}
        </div>
      )}
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#378ADD] hover:underline inline-flex items-center gap-1 mt-1">
          {t("saas.metacognition.source", { defaultValue: "Source" })} <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
      {item.sectors && item.sectors.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mt-2">
          {item.sectors.map((s) => (
            <span key={s} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-500">{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 2 — Positionnement marché
// ============================================================================
function MarketTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/metacognition/market");
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e, t("saas.metacognition.err_load", { defaultValue: "Chargement impossible" })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const regen = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/metacognition/market/regenerate");
      notify.success(t("saas.metacognition.toast_market_regen_started", { defaultValue: "Analyse marché en cours (1-2 min)…" }));
      _pollScanComplete().then(() => load());
    } catch (e) { notify.error(fmtError(e, t("saas.metacognition.err_regen", { defaultValue: "Régénération impossible" }))); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-zinc-500 text-xs py-10 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }

  const latest = data?.latest;

  return (
    <div className="space-y-4" data-testid="metacog-market-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={regen} disabled={busy} className="btn-primary text-xs" data-testid="metacog-regen-market">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {t("saas.metacognition.regenerate", { defaultValue: "Régénérer l'analyse" })}
        </button>
        {latest?.created_at && (
          <span className="text-[10px] text-zinc-500 ml-auto">
            {t("saas.metacognition.last_run", { defaultValue: "Dernière analyse" })} : {new Date(latest.created_at).toLocaleString("fr-FR")}
          </span>
        )}
      </div>

      {!latest ? (
        <div className="noam-card p-8 text-center text-zinc-500 text-sm" data-testid="metacog-market-empty">
          <Target className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          {t("saas.metacognition.empty_market", { defaultValue: "Aucune analyse encore. Lance une première analyse." })}
        </div>
      ) : (
        <>
          {/* Résumé */}
          {latest.summary && (
            <div className="noam-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] mb-1">
                {t("saas.metacognition.executive_summary", { defaultValue: "Synthèse" })}
              </div>
              <p className="text-sm text-zinc-200">{latest.summary}</p>
            </div>
          )}

          {/* Carte de positionnement */}
          <PositioningMap axes={latest.axes || []} scores={latest.positioning_scores || {}} />

          {/* V2 panel — WHY NOAM ? (4 tests) */}
          <WhyNoamSection data={latest.why_noam} />

          {/* V2 panel — Matrice de capacités (Self-State × panel) */}
          <CapabilityMatrixSection matrix={latest.capability_matrix} competitors={latest.competitors} selfStateVersion={latest.self_state_version} />

          {/* V2 panel — Les 3 paris d'excellence */}
          <MustBecomeSection items={latest.must_become_exceptional} />

          {/* V22.7 — Section "Ce que Noam fait que personne d'autre ne fait" */}
          {latest.unique_value_proposition && Object.keys(latest.unique_value_proposition).length > 0 && (
            <div className="noam-card p-4" data-testid="metacog-uvp-section">
              <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] mb-3 flex items-center gap-1">
                <Brain className="w-3 h-3" />
                {t("saas.metacognition.uvp_title", { defaultValue: "Ce que Noam fait que personne d'autre ne fait" })}
              </div>
              <div className="space-y-3">
                {latest.unique_value_proposition.axis_problem_framing && (
                  <div className="border-l-2 border-[#A78BFA] pl-3" data-testid="metacog-uvp-axis-1">
                    <div className="text-[11px] uppercase tracking-wider text-[#A78BFA] font-semibold mb-1">
                      {t("saas.metacognition.uvp_axis_1", { defaultValue: "1. Distinguer les problèmes mal posés des problèmes bien posés" })}
                    </div>
                    <p className="text-xs text-zinc-200 leading-relaxed">{latest.unique_value_proposition.axis_problem_framing}</p>
                  </div>
                )}
                {latest.unique_value_proposition.axis_doctrine_confrontation && (
                  <div className="border-l-2 border-[#1D9E75] pl-3" data-testid="metacog-uvp-axis-2">
                    <div className="text-[11px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-1">
                      {t("saas.metacognition.uvp_axis_2", { defaultValue: "2. Incarner une doctrine et la confronter au réel" })}
                    </div>
                    <p className="text-xs text-zinc-200 leading-relaxed">{latest.unique_value_proposition.axis_doctrine_confrontation}</p>
                  </div>
                )}
                {latest.unique_value_proposition.axis_human_scale && (
                  <div className="border-l-2 border-[#EF9F27] pl-3" data-testid="metacog-uvp-axis-3">
                    <div className="text-[11px] uppercase tracking-wider text-[#EF9F27] font-semibold mb-1">
                      {t("saas.metacognition.uvp_axis_3", { defaultValue: "3. Opérer à l'échelle humaine" })}
                    </div>
                    <p className="text-xs text-zinc-200 leading-relaxed">{latest.unique_value_proposition.axis_human_scale}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Arguments commerciaux */}
            <div className="noam-card p-4" data-testid="metacog-commercial-args">
              <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] mb-2 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3" /> {t("saas.metacognition.commercial_args", { defaultValue: "Arguments commerciaux à exploiter" })}
              </div>
              {latest.commercial_args?.length > 0 ? (
                <ul className="space-y-1.5">
                  {latest.commercial_args.map((a, i) => (
                    <li key={i} className="text-xs text-zinc-200 flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-[#1D9E75] mt-0.5 shrink-0" /> {a}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-zinc-500 italic">—</div>
              )}
            </div>

            {/* Menaces */}
            <div className="noam-card p-4" data-testid="metacog-threats">
              <div className="text-[10px] uppercase tracking-wider text-[#E24B4A] mb-2 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> {t("saas.metacognition.threats", { defaultValue: "Menaces concurrentielles" })}
              </div>
              {latest.threats?.length > 0 ? (
                <ul className="space-y-1.5">
                  {latest.threats.map((th, i) => (
                    <li key={i} className="text-xs text-zinc-200 flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-[#E24B4A] mt-0.5 shrink-0" />
                      <div>
                        <span className="text-zinc-100 font-medium">{th.competitor}</span>
                        <span className="text-[10px] text-zinc-500 ml-1">· {th.urgency}</span>
                        <div className="text-zinc-400 text-[11px]">{th.threat}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-zinc-500 italic">—</div>
              )}
            </div>
          </div>

          {/* Recommandations */}
          {latest.recommendations?.length > 0 && (
            <div className="noam-card p-4" data-testid="metacog-recommendations">
              <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {t("saas.metacognition.recommendations", { defaultValue: "Angles d'attaque commerciaux recommandés" })}
              </div>
              <ul className="space-y-1.5">
                {latest.recommendations.map((r, i) => (
                  <li key={i} className="text-xs text-zinc-200">→ {r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Pitches par type de client */}
          {latest.pitches_by_client && Object.keys(latest.pitches_by_client).length > 0 && (
            <div className="noam-card p-4" data-testid="metacog-pitches">
              <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-3 flex items-center gap-1">
                <FileText className="w-3 h-3" /> {t("saas.metacognition.pitches", { defaultValue: "Pitches par type de client" })}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(latest.pitches_by_client).map(([client, pitch]) => (
                  <div key={client} className="bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`metacog-pitch-${client}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-1">
                      {client.replaceAll("_", " ")}
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">{pitch}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// V2 panel — WHY NOAM ? / Matrice de capacités / Paris d'excellence
// ============================================================================
const GAP_META = {
  ahead: { label: "En avance", color: "#1D9E75" },
  comparable: { label: "Comparable", color: "#378ADD" },
  behind: { label: "En retard", color: "#EF9F27" },
  far_behind: { label: "Très en retard", color: "#E24B4A" },
  unknown: { label: "Indéterminé", color: "#71717A" },
};

const EVIDENCE_META = {
  USED_IN_REAL_OPERATIONS: { label: "Opérations réelles", color: "#1D9E75" },
  ADVERSARIALLY_FALSIFIED: { label: "Falsifié adversarial", color: "#22D3EE" },
  TESTED_ON_REAL_DATA: { label: "Testé données réelles", color: "#378ADD" },
  TECHNICALLY_DEMONSTRATED: { label: "Démontré techniquement", color: "#A78BFA" },
  EXISTS: { label: "Existe (code)", color: "#EF9F27" },
  ABSENT: { label: "Absent", color: "#E24B4A" },
};

function ScoreCell({ value, note }) {
  if (value === "UNKNOWN" || value === null || value === undefined) {
    return <span className="text-zinc-600" title="Information publique insuffisante — comparaison non défendable">?</span>;
  }
  const v = Number(value);
  const color = v >= 4 ? "#1D9E75" : v >= 3 ? "#378ADD" : v >= 2 ? "#EF9F27" : "#E24B4A";
  return <span style={{ color }} className="font-medium" title={note || ""}>{v}</span>;
}

function CapabilityMatrixSection({ matrix, competitors, selfStateVersion }) {
  if (!matrix?.length) return null;
  const comps = competitors?.length ? competitors : Object.keys(matrix[0]?.competitors || {});
  return (
    <div className="noam-card p-4" data-testid="metacog-capability-matrix">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] flex items-center gap-1">
          <Layers className="w-3 h-3" /> Matrice de capacités — NOAM vs panel
        </div>
        <div className="text-[10px] text-zinc-500">
          NOAM évalué sur le Self-State{selfStateVersion ? ` v${selfStateVersion}` : ""} (niveaux de preuve réels) · concurrents sur sources publiques · « ? » = comparaison non défendable
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="capability-matrix-table">
          <thead className="text-[10px] text-zinc-500 border-b border-white/10">
            <tr>
              <th className="text-left py-2 pr-3 font-normal min-w-[180px]">Domaine</th>
              <th className="text-center py-2 px-2 font-medium text-[#A78BFA]">NOAM</th>
              {comps.map((c) => (
                <th key={c} className="text-center py-2 px-2 font-normal whitespace-nowrap">{c.replace(" Fabric IQ", " Fabric").replace(" Agentforce", "").replace(" AIP", "")}</th>
              ))}
              <th className="text-center py-2 px-2 font-normal">Écart NOAM</th>
              <th className="text-center py-2 pl-2 font-normal">Priorité</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row) => {
              const gap = GAP_META[row.gap] || GAP_META.unknown;
              const ev = EVIDENCE_META[row.noam?.evidence] || null;
              return (
                <tr key={row.domain} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`capmx-row-${row.domain}`}>
                  <td className="py-1.5 pr-3 text-zinc-300" title={row.noam?.note || ""}>{row.domain}</td>
                  <td className="text-center px-2">
                    <div className="inline-flex flex-col items-center gap-0.5">
                      <ScoreCell value={row.noam?.score} note={row.noam?.note} />
                      {ev && (
                        <span className="text-[8px] uppercase tracking-wide px-1 rounded" style={{ color: ev.color, background: `${ev.color}14` }} title={row.noam?.note || ""}>
                          {ev.label}
                        </span>
                      )}
                    </div>
                  </td>
                  {comps.map((c) => {
                    const cell = row.competitors?.[c];
                    return <td key={c} className="text-center px-2"><ScoreCell value={cell?.score} note={cell?.note} /></td>;
                  })}
                  <td className="text-center px-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ color: gap.color, background: `${gap.color}1A` }}>{gap.label}</span>
                  </td>
                  <td className="text-center pl-2 text-zinc-300">{row.priority && row.priority !== "-" ? row.priority : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WhyNoamSection({ data }) {
  if (!data || (!data.verdict && !(data.differences || []).length)) return null;
  const diffs = data.differences || [];
  return (
    <div className="noam-card p-4 border border-[#A78BFA]/25" data-testid="metacog-why-noam">
      <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] mb-2 flex items-center gap-1">
        <Crown className="w-3 h-3" /> WHY NOAM ? — différences passant les 4 tests
      </div>
      <div className="text-[10px] text-zinc-500 mb-3">
        Tests : démontrée chez NOAM (Self-State) + réellement différente + utile pour un client + difficile à reproduire
      </div>
      {diffs.length === 0 ? (
        <div className="text-xs text-[#EF9F27] italic" data-testid="why-noam-none">
          Aucune différence ne passe encore les 4 tests simultanément.
        </div>
      ) : (
        <div className="space-y-3">
          {diffs.map((d, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`why-noam-diff-${i}`}>
              <div className="text-xs text-zinc-100 font-medium mb-1.5">{d.difference}</div>
              <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-zinc-400">
                {d.evidence_noam && <div><span className="text-[#1D9E75]">Preuve NOAM :</span> {d.evidence_noam}</div>}
                {d.really_different && <div><span className="text-[#378ADD]">Vraiment différent :</span> {d.really_different}</div>}
                {d.client_value && <div><span className="text-[#EF9F27]">Valeur client :</span> {d.client_value}</div>}
                {d.hard_to_reproduce && <div><span className="text-[#A78BFA]">Difficile à répliquer :</span> {d.hard_to_reproduce}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {data.verdict && <p className="text-xs text-zinc-200 mt-3 leading-relaxed" data-testid="why-noam-verdict">{data.verdict}</p>}
    </div>
  );
}

function MustBecomeSection({ items }) {
  if (!items?.length) return null;
  return (
    <div className="noam-card p-4" data-testid="metacog-must-become">
      <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-3 flex items-center gap-1">
        <Zap className="w-3 h-3" /> Ce que NOAM doit devenir exceptionnellement bon à faire (max 3)
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {items.slice(0, 3).map((it, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`must-become-${i}`}>
            <div className="text-xs text-zinc-100 font-medium mb-1">{i + 1}. {it.capability}</div>
            {it.rationale && <p className="text-[11px] text-zinc-400 leading-relaxed">{it.rationale}</p>}
            {it.michelin_effect && (
              <p className="text-[11px] text-[#EF9F27]/90 mt-1.5 leading-relaxed">★ {it.michelin_effect}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Composant Carte de positionnement (radar visuel simple SVG)
// ============================================================================
// ============================================================================
// Composant Carte de positionnement (radar visuel SVG + toggles + actions)
// ============================================================================
// Mapping axe → module Noam à améliorer si Noam est faible sur l'axe.
const AXIS_TO_MODULE = {
  "Profondeur analytique": { path: "/app/cartographies", label: "Cartographies", reason: "Approfondir les modèles d'analyse" },
  "Rapidité de déploiement": { path: "/app/origination", label: "Origination", reason: "Réduire le time-to-value sur les pipelines" },
  "Intelligence doctrinale": { path: "/app/doctrine", label: "Doctrine", reason: "Renforcer le moteur doctrinal" },
  "Accessibilité prix": { path: "/app/positionnement", label: "Positionnement", reason: "Affiner le pricing et le positionnement" },
  "Couverture sectorielle": { path: "/app/annuaire", label: "Annuaire", reason: "Étendre la couverture des entités/secteurs" },
};

function PositioningMap({ axes, scores }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allCompetitors = Object.keys(scores || {});
  const colorMap = {
    "Noam": "#A78BFA",
    "Palantir AIP": "#E24B4A",
    "Microsoft Fabric IQ": "#378ADD",
    "Celonis": "#EF9F27",
    "ServiceNow": "#1D9E75",
    "Salesforce Agentforce": "#22D3EE",
    "Smartcockpit": "#F472B6",
    "Palantir Foundry": "#E24B4A",
    "BlackRock Aladdin": "#EF9F27",
    "Sigma Computing": "#378ADD",
    "Causal": "#1D9E75",
    "Hex": "#22D3EE",
  };
  // V22.3 — toggle visibilité par concurrent (Noam toujours visible)
  const [hidden, setHidden] = useState(new Set());
  const toggleCompetitor = (comp) => {
    if (comp === "Noam") return; // jamais cachable
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(comp)) next.delete(comp); else next.add(comp);
      return next;
    });
  };
  const visibleCompetitors = allCompetitors.filter((c) => !hidden.has(c));

  if (!axes.length || !allCompetitors.length) {
    return null;
  }

  // V22.3 — SVG plus grand + plus de padding pour les labels longs
  const size = 460;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 80; // labels space
  const n = axes.length;

  const pointFor = (axisIdx, valuePct) => {
    const angle = (Math.PI * 2 * axisIdx) / n - Math.PI / 2;
    const r = (radius * Math.max(0, Math.min(100, valuePct))) / 100;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  // V22.3 — détecter les axes faibles de Noam (< 70/100) pour suggérer des actions
  const noamScores = scores["Noam"] || {};
  const weakAxes = axes
    .map((ax) => ({ axis: ax, score: _scoreForAxis(noamScores, ax) }))
    .filter((a) => a.score < 75)
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  return (
    <div className="noam-card p-4" data-testid="metacog-positioning-map">
      <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] mb-3 flex items-center gap-1">
        <Target className="w-3 h-3" /> {t("saas.metacognition.positioning_map", { defaultValue: "Carte de positionnement Noam vs concurrents" })}
      </div>
      <div className="flex flex-col lg:flex-row items-start gap-6">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 mx-auto lg:mx-0">
          {/* Grilles concentriques */}
          {[0.25, 0.5, 0.75, 1].map((f, i) => (
            <polygon
              key={i}
              points={axes.map((_, idx) => {
                const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
                const r = radius * f;
                return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
              }).join(" ")}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />
          ))}
          {/* Axes + labels (multi-mots split pour ne pas déborder) */}
          {axes.map((axis, idx) => {
            const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
            const x2 = cx + radius * Math.cos(angle);
            const y2 = cy + radius * Math.sin(angle);
            const lx = cx + (radius + 18) * Math.cos(angle);
            const ly = cy + (radius + 18) * Math.sin(angle);
            const ta = Math.abs(Math.cos(angle)) < 0.15 ? "middle" : (Math.cos(angle) > 0 ? "start" : "end");
            // Split en 2 lignes si > 14 chars
            const words = axis.split(" ");
            let line1 = axis;
            let line2 = "";
            if (axis.length > 14 && words.length >= 2) {
              const mid = Math.ceil(words.length / 2);
              line1 = words.slice(0, mid).join(" ");
              line2 = words.slice(mid).join(" ");
            }
            return (
              <g key={axis}>
                <line x1={cx} y1={cy} x2={x2} y2={y2} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                <text
                  x={lx} y={ly} fontSize="10" fill="#d4d4d8"
                  textAnchor={ta} dominantBaseline="middle"
                >
                  <tspan x={lx} dy={line2 ? "-0.5em" : 0}>{line1}</tspan>
                  {line2 && <tspan x={lx} dy="1.1em">{line2}</tspan>}
                </text>
              </g>
            );
          })}
          {/* Polygones par concurrent visible */}
          {visibleCompetitors.map((comp) => {
            const compScores = scores[comp] || {};
            const points = axes.map((ax, idx) => {
              const v = _scoreForAxis(compScores, ax);
              const [x, y] = pointFor(idx, v);
              return `${x},${y}`;
            }).join(" ");
            const col = colorMap[comp] || "#71717A";
            const isNoam = comp === "Noam";
            return (
              <g key={comp}>
                <polygon
                  points={points}
                  fill={`${col}${isNoam ? "33" : "1A"}`}
                  stroke={col}
                  strokeWidth={isNoam ? "2.5" : "1.5"}
                />
              </g>
            );
          })}
        </svg>

        <div className="flex-1 min-w-0 w-full space-y-3">
          {/* Légende avec toggles */}
          <div className="space-y-1" data-testid="metacog-positioning-legend">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              {t("saas.metacognition.legend_title", { defaultValue: "Concurrents — cliquer pour masquer" })}
            </div>
            {allCompetitors.map((comp) => {
              const col = colorMap[comp] || "#71717A";
              const isHidden = hidden.has(comp);
              const isNoam = comp === "Noam";
              return (
                <button
                  key={comp}
                  onClick={() => toggleCompetitor(comp)}
                  disabled={isNoam}
                  className={`w-full flex items-center gap-2 text-xs px-2 py-1 rounded transition ${
                    isHidden ? "opacity-40" : "hover:bg-white/[0.03]"
                  } ${isNoam ? "cursor-default" : "cursor-pointer"}`}
                  data-testid={`metacog-toggle-${comp.replace(/\s+/g, "-")}`}
                  title={isNoam ? "Noam toujours visible" : (isHidden ? "Cliquer pour afficher" : "Cliquer pour masquer")}
                >
                  <span className="w-3 h-3 rounded shrink-0" style={{ background: col, opacity: isHidden ? 0.3 : 1 }} />
                  <span className={isNoam ? "text-zinc-100 font-semibold" : "text-zinc-300"}>{comp}</span>
                  {!isNoam && (
                    <span className="ml-auto">
                      {isHidden ? <EyeOff className="w-3 h-3 text-zinc-600" /> : <Eye className="w-3 h-3 text-zinc-500" />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* V22.3 — Actions stratégiques basées sur les axes faibles */}
          {weakAxes.length > 0 && (
            <div className="border-t border-white/10 pt-3" data-testid="metacog-weak-actions">
              <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-2 flex items-center gap-1">
                <Zap className="w-3 h-3" /> {t("saas.metacognition.improve_via", { defaultValue: "Axes faibles — amélioration immédiate" })}
              </div>
              <div className="space-y-1.5">
                {weakAxes.map((w) => {
                  const target = AXIS_TO_MODULE[w.axis];
                  if (!target) return null;
                  return (
                    <button
                      key={w.axis}
                      onClick={() => navigate(target.path)}
                      className="w-full text-left px-2 py-1.5 rounded bg-white/[0.02] border border-white/5 hover:border-[#7F77DD]/40 hover:bg-[#7F77DD]/[0.04] transition group"
                      data-testid={`metacog-action-${w.axis.replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="text-zinc-300 truncate flex-1">{w.axis}</span>
                        <span className="text-[#EF9F27] font-mono">{w.score}/100</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 group-hover:text-zinc-400 mt-0.5 flex items-center gap-1">
                        <span className="truncate">{target.reason}</span>
                        <ArrowUpRight className="w-2.5 h-2.5 text-[#7F77DD] shrink-0" />
                        <span className="text-[#7F77DD]">{target.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* V22.3 — Actions transverses */}
          <div className="border-t border-white/10 pt-3 flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => navigate("/app/utility-mapping")}
              className="text-[10px] px-2 py-1 rounded border border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75] hover:bg-[#1D9E75]/20 inline-flex items-center gap-1"
              data-testid="metacog-action-utility-map"
              title={t("saas.metacognition.utility_map_title", { defaultValue: "Créer une cartographie des dépendances depuis ce positionnement" })}
            >
              <Layers className="w-3 h-3" /> {t("saas.metacognition.action_utility_map", { defaultValue: "Cartographier les dépendances" })}
            </button>
            <button
              onClick={() => navigate("/app/cartographies")}
              className="text-[10px] px-2 py-1 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 inline-flex items-center gap-1"
              data-testid="metacog-action-cartographies"
            >
              <Target className="w-3 h-3" /> {t("saas.metacognition.action_carto360", { defaultValue: "Carto 360°" })}
            </button>
            <button
              onClick={() => navigate("/app/radar")}
              className="text-[10px] px-2 py-1 rounded border border-[#378ADD]/40 bg-[#378ADD]/10 text-[#378ADD] hover:bg-[#378ADD]/20 inline-flex items-center gap-1"
              data-testid="metacog-action-radar"
            >
              <RefreshCw className="w-3 h-3" /> {t("saas.metacognition.action_radar", { defaultValue: "Noam Radar" })}
            </button>
            <button
              onClick={() => navigate("/app/url-scanner")}
              className="text-[10px] px-2 py-1 rounded border border-zinc-500/40 bg-white/[0.02] text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1"
              data-testid="metacog-action-url-scanner"
              title={t("saas.metacognition.scan_competitor_title", { defaultValue: "Scanner le site d'un concurrent pour enrichir l'analyse" })}
            >
              <ExternalLink className="w-3 h-3" /> {t("saas.metacognition.action_scan_competitor", { defaultValue: "Scanner un concurrent" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// V22.2 — SpecDrawer : génère et affiche une spec technique NOAM
// ============================================================================
function SpecDrawer({ improvementId, improvementTitle, onClose }) {
  const { t } = useTranslation();
  const [specs, setSpecs] = useState([]);
  const [active, setActive] = useState(null); // spec doc actuel
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Load existing specs
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/metacognition/improvements/${improvementId}/specs`);
      const list = r.data?.items || [];
      setSpecs(list);
      if (list.length > 0 && !active) setActive(list[0]);
    } catch (e) {
      notify.error(fmtError(e, t("saas.metacognition.spec_err_load", { defaultValue: "Chargement spec impossible" })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [improvementId]);

  // Polling si une spec est pending
  useEffect(() => {
    if (!active || active.status !== "pending") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/workspace/metacognition/specs/${active.id}`);
        if (r.data?.status !== "pending") {
          setActive(r.data);
          setSpecs((prev) => prev.map((s) => s.id === r.data.id ? r.data : s));
          if (r.data.status === "ready") notify.success(t("saas.metacognition.spec_ready", { defaultValue: "Spec prête ✓" }));
          else if (r.data.status === "failed") notify.error(t("saas.metacognition.spec_failed", { defaultValue: "Génération spec échouée" }));
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(id);
  }, [active, t]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/workspace/metacognition/improvements/${improvementId}/generate-spec`);
      const specId = r.data?.spec_id;
      notify.success(t("saas.metacognition.spec_started", { defaultValue: "Génération lancée (30-60s)" }));
      // Récupère le doc spec pending pour le polling
      const sp = await api.get(`/workspace/metacognition/specs/${specId}`);
      setActive(sp.data);
      setSpecs((prev) => [sp.data, ...prev]);
    } catch (e) { notify.error(fmtError(e, t("saas.metacognition.spec_err_gen", { defaultValue: "Génération impossible" }))); }
    finally { setGenerating(false); }
  };

  const exportMd = async () => {
    if (!active || active.status !== "ready") return;
    try {
      const r = await api.get(`/workspace/metacognition/specs/${active.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `noam-spec-${(improvementTitle || "spec").replace(/\s+/g, "_").slice(0, 60)}.md`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const markStatus = async (status) => {
    if (!active) return;
    try {
      const r = await api.patch(`/workspace/metacognition/specs/${active.id}`, { status });
      setActive(r.data);
      setSpecs((prev) => prev.map((s) => s.id === r.data.id ? r.data : s));
      notify.success(t("saas.metacognition.spec_status_updated", { defaultValue: "Statut mis à jour" }));
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid="metacog-spec-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full sm:w-[640px] lg:w-[760px] bg-[#0a0a0a] border-l border-white/10 overflow-y-auto shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#7F77DD] flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {t("saas.metacognition.spec_overline", { defaultValue: "Spec technique IA" })}
              </div>
              <h2 className="text-base font-semibold text-zinc-100 mt-1 line-clamp-2">{improvementTitle}</h2>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 p-1" data-testid="metacog-spec-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button onClick={generate} disabled={generating || (active && active.status === "pending")} className="btn-primary text-xs disabled:opacity-50" data-testid="metacog-spec-generate">
              {generating || (active?.status === "pending") ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {specs.length > 0
                ? t("saas.metacognition.spec_regenerate", { defaultValue: "Régénérer" })
                : t("saas.metacognition.spec_generate", { defaultValue: "Générer la spec" })}
            </button>
            {active && active.status === "ready" && (
              <>
                <button onClick={exportMd} className="btn-ghost text-xs" data-testid="metacog-spec-export">
                  <Download className="w-3 h-3" /> {t("saas.metacognition.spec_export", { defaultValue: "Export .md" })}
                </button>
                <div className="flex items-center gap-1 ml-auto text-[10px]">
                  <span className="text-zinc-500">{t("saas.metacognition.spec_impl_status", { defaultValue: "Statut impl." })} :</span>
                  {["draft", "reviewed", "implemented"].map((s) => (
                    <button
                      key={s}
                      onClick={() => markStatus(s)}
                      className={`px-2 py-0.5 rounded uppercase tracking-wider transition ${
                        active.implementation_status === s
                          ? "bg-[#7F77DD]/20 text-[#7F77DD] border border-[#7F77DD]/40"
                          : "border border-white/10 text-zinc-500 hover:text-zinc-300"
                      }`}
                      data-testid={`metacog-spec-status-${s}`}
                    >
                      {s === "draft" ? "Brouillon" : s === "reviewed" ? "Revue" : "Implémenté"}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {specs.length > 1 && (
            <div className="flex items-center gap-1 mt-3 overflow-x-auto pb-1">
              <History className="w-3 h-3 text-zinc-500 shrink-0" />
              {specs.map((sp, i) => (
                <button
                  key={sp.id}
                  onClick={() => setActive(sp)}
                  className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${active?.id === sp.id ? "bg-[#7F77DD]/20 text-[#7F77DD]" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid={`metacog-spec-history-${i}`}
                >
                  v{specs.length - i} · {new Date(sp.created_at).toLocaleDateString("fr-FR")}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 px-5 py-4">
          {loading ? (
            <div className="text-center text-zinc-500 text-xs py-10">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> {t("common.loading", { defaultValue: "Chargement…" })}
            </div>
          ) : !active ? (
            <div className="text-center text-zinc-500 text-sm py-12" data-testid="metacog-spec-empty">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
              {t("saas.metacognition.spec_empty", {
                defaultValue: "Aucune spec encore. Clique sur « Générer la spec » — NOAM analysera la suggestion et produira user stories, modèle de données, endpoints, composants, critères d'acceptation et risques.",
              })}
            </div>
          ) : active.status === "pending" ? (
            <div className="text-center text-zinc-400 text-sm py-12" data-testid="metacog-spec-pending">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[#7F77DD]" />
              <div>{t("saas.metacognition.spec_pending", { defaultValue: "NOAM analyse la suggestion…" })}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{t("saas.metacognition.spec_pending_sub", { defaultValue: "30-60 secondes" })}</div>
            </div>
          ) : active.status === "failed" ? (
            <div className="text-center text-[#E24B4A] text-sm py-12" data-testid="metacog-spec-failed">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
              <div>{t("saas.metacognition.spec_err", { defaultValue: "Génération échouée" })}</div>
              {active.error && <div className="text-[11px] text-zinc-500 mt-2">{active.error}</div>}
            </div>
          ) : (
            <div
              className="prose prose-invert prose-sm max-w-none text-zinc-200"
              data-testid="metacog-spec-content"
              style={{ whiteSpace: "pre-wrap", fontFamily: "ui-monospace, monospace", fontSize: "12px", lineHeight: "1.6" }}
            >
              {active.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================================================================
   SelfAsymTab — Méta-asymétries de Noam (V23.14.7d — Volet B)
   Doctrine 47 inversée appliquée à Noam : Noam se cartographie lui-même
   avant de juger les autres. Forces · Faiblesses · Pitch concurrent.
   ==================================================================== */
function SelfAsymTab() {
  const [phase, setPhase] = useState("idle"); // idle | running | done | failed
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [competitorFocus, setCompetitorFocus] = useState("");
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/metacognition/self-asymetries/latest");
        if (!cancelled && r.data?.available) {
          setAnalysis(r.data);
          setPhase("done");
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; stopPolling(); };
  }, []);

  const startAnalysis = async () => {
    setPhase("running"); setError(null); setProgress("Démarrage…");
    try {
      const r = await api.post("/workspace/metacognition/self-asymetries/start", {
        competitor_focus: competitorFocus.trim() || null,
      });
      const job_id = r.data?.job_id;
      if (!job_id) throw new Error("Pas de job_id");
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/workspace/metacognition/self-asymetries/jobs/${job_id}`);
          const d = s.data || {};
          setProgress(d.progress || "");
          if (d.status === "done") {
            stopPolling();
            const latest = await api.get("/workspace/metacognition/self-asymetries/latest");
            if (latest.data?.available) setAnalysis(latest.data);
            setPhase("done");
            notify.success("Méta-asymétries de Noam générées.");
          } else if (d.status === "failed") {
            stopPolling(); setError(d.error || "Erreur"); setPhase("failed");
          }
        } catch (e) { stopPolling(); setError(fmtError(e)); setPhase("failed"); }
      }, 2000);
    } catch (e) {
      setError(fmtError(e)); setPhase("failed");
    }
  };

  const strengths = analysis?.strengths || [];
  const weaknesses = analysis?.weaknesses || [];
  const projections = analysis?.competitor_projections || [];

  return (
    <div className="space-y-5" data-testid="metacog-self-asym-tab">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#7F77DD]/15 border border-[#7F77DD]/30 flex items-center justify-center flex-shrink-0">
          <Brain className="w-5 h-5 text-[#7F77DD]" />
        </div>
        <div className="flex-1">
          <div className="noam-overline">Doctrine 47 · Méta-asymétries</div>
          <div className="noam-display text-lg font-semibold">Asymétries de Noam (forces & faiblesses face aux concurrents)</div>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl leading-relaxed">
            Avant que Noam juge les autres, il se cartographie lui-même. Forces structurelles défendables · faiblesses honnêtes · pitch commercial face à chaque outil concurrent. Honnêteté absolue exigée (humilité doctrinale).
          </p>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[260px] max-w-md">
          <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Comparer à un concurrent précis (facultatif)</label>
          <input
            type="text"
            value={competitorFocus}
            onChange={(e) => setCompetitorFocus(e.target.value)}
            placeholder="Ex: Salesforce Einstein, Notion AI…"
            className="mt-1 w-full px-3 py-1.5 text-sm bg-white/[0.04] border border-white/10 rounded text-zinc-200 placeholder:text-zinc-600 focus:border-[#7F77DD]/50 focus:outline-none"
            data-testid="metacog-self-asym-focus-input"
          />
        </div>
        <button
          onClick={startAnalysis}
          disabled={phase === "running"}
          className="btn-primary text-sm disabled:opacity-50"
          data-testid="metacog-self-asym-start-btn"
        >
          {phase === "running" ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {progress || "Analyse…"}</>
          ) : phase === "done" ? (
            <><RefreshCw className="w-4 h-4" /> Relancer</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Lancer la méta-analyse</>
          )}
        </button>
        {analysis && (
          <FreshnessBadge
            lastGeneratedAt={analysis.created_at}
            moduleLabel="Méta-asymétries Noam"
            testid="metacog-self-asym-freshness"
          />
        )}
      </div>

      {phase === "failed" && error && (
        <div className="p-3 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-sm text-[#E24B4A]" data-testid="metacog-self-asym-error">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
        </div>
      )}

      {phase === "done" && analysis && (
        <div className="space-y-4" data-testid="metacog-self-asym-results">
          {/* UVP */}
          {analysis.unique_value_proposition && (
            <div className="border border-[#EFD127]/30 bg-[#EFD127]/[0.06] rounded-md p-4">
              <div className="noam-overline mb-1.5 flex items-center gap-1.5">
                <Crown className="w-3 h-3 text-[#EFD127]" /> Proposition de valeur unique
              </div>
              <div className="text-base text-zinc-100 font-medium leading-relaxed" data-testid="metacog-self-asym-uvp">
                « {analysis.unique_value_proposition} »
              </div>
            </div>
          )}

          {/* Forces / Faiblesses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-[#1D9E75]/30 rounded-md p-4">
              <div className="noam-overline mb-2 text-[#1D9E75]">Forces structurelles ({strengths.length})</div>
              <ul className="space-y-2.5" data-testid="metacog-self-asym-strengths">
                {strengths.map((s, i) => (
                  <li key={i} className="text-sm" data-testid={`metacog-self-asym-strength-${i}`}>
                    <div className="text-zinc-100 font-medium flex items-center gap-2 flex-wrap">
                      {s.title}
                      {s.defendability_0_100 != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1D9E75]/15 text-[#1D9E75] font-bold">
                          Défense {s.defendability_0_100}/100
                        </span>
                      )}
                      {s.asymetry_type && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 italic">{s.asymetry_type}</span>
                      )}
                    </div>
                    <div className="text-[12px] text-zinc-400 mt-0.5 leading-relaxed">{s.description}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#EF9F27]/30 rounded-md p-4">
              <div className="noam-overline mb-2 text-[#EF9F27]">Faiblesses honnêtes ({weaknesses.length})</div>
              <ul className="space-y-2.5" data-testid="metacog-self-asym-weaknesses">
                {weaknesses.map((w, i) => (
                  <li key={i} className="text-sm" data-testid={`metacog-self-asym-weakness-${i}`}>
                    <div className="text-zinc-100 font-medium flex items-center gap-2">
                      {w.title}
                      {w.severity_0_100 != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] font-bold">
                          Sévérité {w.severity_0_100}/100
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-zinc-400 mt-0.5 leading-relaxed">{w.description}</div>
                    {w.mitigation && (
                      <div className="text-[11px] text-[#378ADD] mt-0.5">→ Mitigation : {w.mitigation}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pitch concurrent */}
          {projections.length > 0 && (
            <div className="border border-white/10 rounded-md p-4">
              <div className="noam-overline mb-2 flex items-center gap-1.5">
                <Target className="w-3 h-3 text-[#378ADD]" /> Pitch commercial face aux concurrents ({projections.length})
              </div>
              <div className="space-y-2.5">
                {projections.map((p, i) => (
                  <div key={i} className="rounded p-3 bg-white/[0.02] border border-white/5" data-testid={`metacog-self-asym-projection-${i}`}>
                    <div className="text-sm font-medium text-zinc-100 mb-1">{p.name}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      <div className="text-[#EF9F27]">
                        <span className="font-semibold">Ils gagnent :</span> {p.where_they_win}
                      </div>
                      <div className="text-[#1D9E75]">
                        <span className="font-semibold">Noam gagne :</span> {p.where_noam_wins}
                      </div>
                    </div>
                    {p.recommended_pitch && (
                      <div className="mt-1.5 text-[12px] text-zinc-200 italic border-l-2 border-[#378ADD] pl-2">
                        Pitch : « {p.recommended_pitch} »
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Humility note */}
          {analysis.humility_note && (
            <div className="border border-zinc-700/40 bg-zinc-900/40 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Note d'humilité (limites structurelles)</div>
              <p className="text-[12px] text-zinc-400 leading-relaxed italic" data-testid="metacog-self-asym-humility">
                {analysis.humility_note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

